import { motion } from "framer-motion";
import SEO from "@/components/SEO";

const Cordonnateur = () => {
  return (
    <>
      <SEO
        title="Le Coordinateur"
        description="Découvrez le rôle et les responsabilités du Coordinateur du Projet Mobilier National, son message et sa vision pour le développement de l'artisanat sénégalais."
        keywords="PMN, Coordinateur, Artisanat, Sénégal, Mobilier, Direction, Gestion, Développement"
        ogImage="/images/about.png"
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative h-[50vh] md:h-[60vh] lg:h-[70vh]">
          <div className="absolute inset-0">
            <img
              src="/images/banner-coordonnateur.webp"
              alt="Bannière du Coordonnateur"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/30" />
          </div>
          <div className="relative h-full flex items-center">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-3xl mx-auto text-center"
              >
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                  Le mot du Coordonnateur
                </h1>
                <p className="text-xl text-white/90">MONSIEUR IBRAHIMA TALL</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12 md:py-16 lg:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
                {/* Profile Image */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="lg:col-span-1"
                >
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <img
                      src="/images/president.webp"
                      alt="Monsieur Ibrahima Tall"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </motion.div>

                {/* Message Content */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="lg:col-span-2"
                >
                  <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.8, delay: 0.6 }}
                      className="prose prose-lg max-w-none"
                    >
                      <p className="text-gray-700 mb-6">
                        Chers artisans, partenaires et amis du Projet Mobilier
                        National,
                      </p>
                      <p className="text-gray-700 mb-6">
                        C'est avec une grande fierté que je m'adresse à vous en
                        tant que coordonnateur du Projet Mobilier National. Ce
                        projet, initié par le décret n° 2015-397 du 26 mars 2015
                        et mis à jour par le décret 2023-1021 du 10 mai 2023,
                        constitue une initiative essentielle pour promouvoir et
                        développer notre artisanat local. Notre objectif est de
                        créer un environnement où le talent créatif sénégalais
                        est pleinement intégré dans les commandes publiques,
                        favorisant ainsi la croissance économique durable de
                        notre nation.
                      </p>
                      <p className="text-gray-700 mb-6">
                        La mission principale du projet Mobilier National est de
                        faciliter l'accès des artisans sénégalais aux marchés
                        publics. Pour y parvenir, la formation, la
                        formalisation, le financement, et l'accompagnement
                        financier constituent les axes majeurs. Soutenir les
                        artisans, c'est promouvoir le savoir-faire local.
                      </p>
                      <p className="text-gray-700 mb-6">
                        Nous organisons également un concours de reproduction de
                        prototypes existants pour encourager l'innovation en
                        design.
                      </p>
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <p className="text-gray-900 font-semibold">
                          MONSIEUR IBRAHIMA TALL
                        </p>
                        <p className="text-green-600">
                          COORDONNATEUR DU PROJET MOBILIER NATIONAL
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-16 md:py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              ÉVOLUTION DU PMN : DE LA CULTURE À L'ÉCONOMIE
              </h2>
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-green-600 text-white rounded-full p-3">
                    <div className="w-2 h-2 bg-white rounded-full" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                    1997
                    </h3>
                    <p className="text-gray-600">
                    Création du PMN par le décret n°97-928.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-green-600 text-white rounded-full p-3">
                    <div className="w-2 h-2 bg-white rounded-full" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      2015
                    </h3>
                    <p className="text-gray-600">
                    Le décret n°2015-397 ouvre la commande publique aux artisans.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-green-600 text-white rounded-full p-3">
                    <div className="w-2 h-2 bg-white rounded-full" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      2023
                    </h3>
                    <p className="text-gray-600">
                    Le décret 2023-1021 renforce le PMN et élargit son champ d'actions.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Cordonnateur;
